import { useCallback, useEffect, useMemo, useReducer } from 'react'
import qs from 'qs'
import moment from 'moment'
import { SEARCH_VERTICALS } from 'constants/search'
import { FlightsFareTypes } from 'constants/flight'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { resetFlights } from 'actions/CartActions'
import { pushWithRegion } from 'actions/NavigationActions'
import { validateFlightOccupants } from 'lib/flights'
import { countOccupantsForFlights } from 'lib/occupancyUtils'
import { isFlightsCredit, isStandaloneFlightsEnabled } from 'selectors/flightsSelectors'
import FlightsTabContent from '../Components/Mobile/TabContents/FlightsTabContent'
import flightSearchWidgetStateReducer, { FlightSearchWidgetActions, initialState } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import FlightSearchWidgetStateProvider from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateProvider'
import LinePlaneDepartureIcon from 'components/Luxkit/Icons/line/LinePlaneDepartureIcon'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import * as Analytics from 'analytics/analytics'
import { searchFromHomepageEvent } from 'analytics/optimizely/events'

function useGlobalSearchVerticalTabFlights(searchVerticalItem?: App.SearchVerticalItem): App.GlobalSearchVerticalTabV2 {
  const dispatch = useAppDispatch()

  const isEnabled = useAppSelector((state) => isStandaloneFlightsEnabled(state) || isFlightsCredit(state))
  const pathname = useAppSelector(state => state.router.location.pathname)
  const regionCode = useAppSelector(state => state.geo.currentRegionCode)

  const [state, stateDispatch] = useReducer(flightSearchWidgetStateReducer, {
    ...initialState,
  })

  const {
    departureAirport,
    arrivalAirport,
    checkinDate,
    checkoutDate,
    occupants,
    fareType,
    fareCabin,
    showDepartureError,
    showDestinationError,
    showTravelDatesError,
    showSameAirportError,
  } = state

  const invalidOccupantsSelection = useMemo(() => validateFlightOccupants(occupants), [occupants])

  const invalidDateSelection = useMemo(() => {
    if (fareType.value === 'return') {
      return !checkinDate || !checkoutDate
    } else {
      return !checkinDate
    }
  }, [fareType, checkinDate, checkoutDate])

  const onSearch = useCallback(() => {
    if (!departureAirport?.airportCode) {
      stateDispatch({ type: FlightSearchWidgetActions.SHOW_DEPARTURE_ERROR, error: true })
    }
    if (!arrivalAirport?.airportCode) {
      stateDispatch({ type: FlightSearchWidgetActions.SHOW_ARRIVAL_ERROR, error: true })
    }
    if (invalidDateSelection) {
      stateDispatch({ type: FlightSearchWidgetActions.SHOW_TRAVEL_DATES_ERROR, error: true })
    }
    if (departureAirport?.airportCode === arrivalAirport?.airportCode) {
      stateDispatch({ type: FlightSearchWidgetActions.SHOW_SAME_AIRPORT_ERROR, error: true })
    }
    if (!departureAirport?.airportCode || !arrivalAirport?.airportCode ||
      invalidDateSelection || occupants.childrenAge?.some(age => age === -1) ||
      departureAirport?.airportCode === arrivalAirport?.airportCode || invalidOccupantsSelection)
    {
      return false
    }

    const transformedOccupants = countOccupantsForFlights([occupants])
    const isReturnFare = fareType.value === FlightsFareTypes.RETURN

    const queryParams = {
      originAirportCode: departureAirport.airportCode,
      originAirportName: departureAirport.airportName,
      destinationAirportCode: arrivalAirport.airportCode,
      destinationAirportName: arrivalAirport.airportName,
      departDate: moment(checkinDate).format(ISO_DATE_FORMAT),
      returnDate: isReturnFare ? moment(checkoutDate).format(ISO_DATE_FORMAT) : null,
      adults: transformedOccupants.adults,
      children: transformedOccupants.children,
      infants: transformedOccupants.infants,
      childrenAge: transformedOccupants.childrenAge.join(','),
      fareClass: fareCabin.value,
      fareType: fareType.value,
      searchId: moment().unix(),
    }
    if (pathname === `/${regionCode.toLowerCase()}`) {
      Analytics.track('search-from-homepage', searchFromHomepageEvent())
    }
    dispatch(resetFlights())
    dispatch(pushWithRegion('/flights-search-results', qs.stringify(queryParams)))
  }, [
    departureAirport,
    arrivalAirport,
    invalidDateSelection,
    occupants,
    invalidOccupantsSelection,
    fareType.value,
    checkinDate,
    checkoutDate,
    fareCabin.value,
    pathname,
    regionCode,
    dispatch,
  ])

  useEffect(() => {
    if (departureAirport?.airportCode && showDepartureError) {
      stateDispatch({ type: FlightSearchWidgetActions.SHOW_DEPARTURE_ERROR, error: false })
    }
    if (arrivalAirport?.airportCode && showDestinationError) {
      stateDispatch({ type: FlightSearchWidgetActions.SHOW_ARRIVAL_ERROR, error: false })
    }
    if (!invalidDateSelection && showTravelDatesError) {
      stateDispatch({ type: FlightSearchWidgetActions.SHOW_TRAVEL_DATES_ERROR, error: false })
    }
    if (departureAirport?.airportCode !== arrivalAirport?.airportCode && showSameAirportError) {
      stateDispatch({ type: FlightSearchWidgetActions.SHOW_SAME_AIRPORT_ERROR, error: false })
    }
  }, [
    departureAirport,
    arrivalAirport,
    checkinDate,
    checkoutDate,
    invalidDateSelection,
    showDepartureError,
    showDestinationError,
    showTravelDatesError,
    showSameAirportError,
  ])

  return useMemo((): App.GlobalSearchVerticalTabV2 => ({
    isEnabled: isEnabled && !!searchVerticalItem,
    label: searchVerticalItem?.label ?? 'flights',
    key: SEARCH_VERTICALS.FLIGHTS,
    context: { dispatch: stateDispatch, state },
    contextProvider: FlightSearchWidgetStateProvider,
    onSearch,
    ContentComponent: FlightsTabContent,
    Icon: LinePlaneDepartureIcon,
  }), [isEnabled, searchVerticalItem, state, onSearch])
}

export default useGlobalSearchVerticalTabFlights
